import { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api, { fmtError, API } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PdfMultiPageViewer } from "../components/documents/PdfMultiPageViewer";
import { CheckCircle, Clock, ArrowDown } from "@phosphor-icons/react";
import { useSiteContent } from "../lib/useSiteContent";

// Renders the signer's typed name in a cursive script font onto an offscreen
// canvas — this becomes the "signature image" stamped into the PDF, same
// format the backend already expects (a signature_data_url PNG), just
// generated from typed text instead of hand-drawing.
async function renderTypedSignature(name) {
  try { await document.fonts.load("700 64px Caveat"); await document.fonts.ready; } catch { /* font API unsupported — falls back to default */ }
  const canvas = document.createElement("canvas");
  canvas.width = 600; canvas.height = 180;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#151522";
  ctx.font = "700 64px 'Caveat', cursive";
  ctx.textBaseline = "middle";
  ctx.fillText(name, 16, canvas.height / 2 + 8);
  return canvas.toDataURL("image/png");
}

export default function DocumentSign() {
  const { docId } = useParams();
  const [searchParams] = useSearchParams();
  const st = searchParams.get("st") || "";
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [typedName, setTypedName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [signing, setSigning] = useState(false);
  const [done, setDone] = useState(false);
  const pageRefs = useRef({});
  const content = useSiteContent();
  const dT = (key, fallback) => content[key] || fallback;

  const jumpToSignature = () => {
    const el = info && pageRefs.current[info.box.page + 1];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  useEffect(() => {
    api.get(`/documents/${docId}/sign-info`, { params: { st } })
      .then((r) => { setInfo(r.data); setTypedName(r.data.signer_name || ""); })
      .catch((e) => setError(e.response?.data?.detail || "This signing link isn't valid."));
  }, [docId, st]);

  const submit = async () => {
    if (!typedName.trim()) { toast.error("Please type your full name to sign."); return; }
    if (!agreed) { toast.error("Please tick 'I agree to the terms in this document' first."); return; }
    setSigning(true);
    try {
      const dataUrl = await renderTypedSignature(typedName.trim());
      const r = await api.post(`/documents/${docId}/sign`, { signature_data_url: dataUrl, typed_name: typedName.trim(), agreed: true }, { params: { st } });
      setDone(r.data.all_signed ? "all" : "next");
      toast.success("Signed!");
    } catch (e) { toast.error(fmtError(e.response?.data?.detail)); } finally { setSigning(false); }
  };

  if (error) return <div className="min-h-screen flex items-center justify-center p-6 text-center" data-testid="doc-sign-error"><p className="text-red-600 font-bold">{error}</p></div>;
  if (!info) return <div className="min-h-screen flex items-center justify-center text-neutral-400">Loading…</div>;

  if (done || info.already_signed) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" data-testid="doc-sign-done">
        <div className="max-w-md text-center">
          <CheckCircle size={48} className="text-emerald-600 mx-auto mb-4" />
          <h1 className="text-xl font-black mb-2">
            {info.already_signed
              ? dT("docsign_already_signed_title", "You've already signed this document.")
              : dT("docsign_success_title", "Signed successfully!")}
          </h1>
          <p className="text-sm text-neutral-500">
            {done === "all" || info.doc_status === "completed"
              ? dT("docsign_success_all_signed_message", "All parties have now signed. A fully signed copy has been emailed to the business.")
              : dT("docsign_success_next_signer_message", "We'll email the next signer now. You'll get a copy once everyone has signed.")}
          </p>
        </div>
      </div>
    );
  }

  if (!info.is_this_signer_turn) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 text-center" data-testid="doc-sign-waiting">
        <div>
          <Clock size={40} className="text-amber-500 mx-auto mb-3" />
          <p className="font-bold">Not your turn yet</p>
          <p className="text-sm text-neutral-500">A previous signer hasn't completed their signature. We'll email you again once it's your turn.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF7F2] p-4 py-8" data-testid="doc-sign-page">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-xl font-black mb-1">{info.title}</h1>
        <p className="text-sm text-neutral-500 mb-1">{info.business_name} · Signer {info.signer_order} of {info.total_signers} — {info.signer_name}</p>
        <p className="text-xs text-neutral-400 mb-4">Please review the full document below before signing ({info.page_count} page{info.page_count > 1 ? "s" : ""}).</p>
        <div className="mb-6 w-full">
          <PdfMultiPageViewer
            file={`${API}/documents/${docId}/pdf?st=${encodeURIComponent(st)}`}
            pageCount={info.page_count}
            width={640}
            pageRefs={pageRefs}
            renderOverlay={(num) => num === info.box.page + 1 && (
              <div className="absolute border-2 border-mango bg-mango/10 flex items-center justify-center text-xs font-bold text-mango pointer-events-none animate-pulse"
                style={{ left: `${info.box.x_pct * 100}%`, top: `${info.box.y_pct * 100}%`, width: `${info.box.w_pct * 100}%`, height: `${info.box.h_pct * 100}%` }}>
                Sign here
              </div>
            )}
          />
        </div>
        <div className="bg-white border-2 border-black p-4">
          <div className="text-xs font-bold uppercase tracking-wide mb-2">Type your full name to sign</div>
          <Input value={typedName} onChange={(e) => setTypedName(e.target.value)} placeholder="Your full name" className="rounded-none mb-3" data-testid="doc-sign-typed-name" />
          <div className="border border-neutral-300 bg-neutral-50 h-24 flex items-center px-4 mb-3" data-testid="doc-sign-preview">
            <span style={{ fontFamily: "'Caveat', cursive", fontSize: "2.75rem", fontWeight: 700 }} className="text-neutral-800 truncate">
              {typedName || "Your signature preview"}
            </span>
          </div>
          <label className="flex items-start gap-2 text-sm mb-4 cursor-pointer select-none">
            <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-0.5 w-4 h-4" data-testid="doc-sign-agree-checkbox" />
            <span>I agree to the terms outlined in this document, and typing my name above constitutes my electronic signature.</span>
          </label>
          <Button onClick={submit} disabled={signing || !typedName.trim() || !agreed} className="rounded-none bg-mango hover:bg-mango/90 w-full disabled:opacity-50" data-testid="doc-sign-submit">
            {signing ? "Submitting…" : "Sign & Submit"}
          </Button>
        </div>
      </div>
      <button
        onClick={jumpToSignature}
        data-testid="doc-sign-jump-button"
        className="fixed bottom-6 right-6 bg-black text-white rounded-full pl-4 pr-5 py-3 shadow-xl flex items-center gap-2 font-bold text-sm hover:bg-mango transition-colors z-50"
      >
        <ArrowDown size={16} weight="bold" /> Jump to signature
      </button>
    </div>
  );
}
